import React, { useEffect, useState, useRef } from "react";
import { Toast } from "primereact/toast";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import AppLoading from "../app-loading/app-loading";
import RoleService from "../../services/user-role/role-service";
import { PermissionKey, SubMenu, Menu, Module, Role } from "./role-permission-types";
import { PermissionType } from "../../types/enum";
import "./role-permissions.css";
import { applicablePermissions, hasAllPermissions, hasPermission, permissionKeys, setAllPermissions, setPermission } from "./permission-actions";
import MenuService from "../../services/menu/menu.service";

// Helper functions for data mapping
function mapToSubMenu(sub: any): SubMenu {
    return {
        menuId: sub.menuId,
        menuName: sub.menuName,
        displayName: sub.displayName,
        icon: sub.icon,
        route: sub.route,
        displayOrder: sub.displayOrder,
        parentMenuId: sub.parentMenuId,
        view: sub.view || false,
        create: sub.create || false,
        edit: sub.edit || false,
        delete: sub.delete || false,
        export: sub.export === true,
        import: sub.import === true,
        supportsExport: sub.supportsExport === true,
        supportsImport: sub.supportsImport === true,
        subMenu: (sub.subMenu || []).map(mapToSubMenu)
    };
}

function mapToMenu(menu: any): Menu {
    return {
        menuId: menu.menuId,
        menuName: menu.menuName,
        displayName: menu.displayName,
        icon: menu.icon,
        route: menu.route,
        displayOrder: menu.displayOrder,
        parentMenuId: menu.parentMenuId,
        view: menu.view || false,
        create: menu.create || false,
        edit: menu.edit || false,
        delete: menu.delete || false,
        export: menu.export === true,
        import: menu.import === true,
        supportsExport: menu.supportsExport === true,
        supportsImport: menu.supportsImport === true,
        subMenu: (menu.subMenu || []).map(mapToSubMenu)
    };
}

const FeatureAccessMain: React.FC = () => {
    const [roles, setRoles] = useState<Role[]>([]);
    const [selectedRole, setSelectedRole] = useState<Role | null>(null);
    const [modules, setModules] = useState<Module[]>([]);
    const [loading, setLoading] = useState(false);
    const toast = useRef<Toast>(null);
    const [collapsedMenus, setCollapsedMenus] = useState<Set<string>>(new Set());
    const [expandedModules, setExpandedModules] = useState<Set<number>>(new Set([0])); // First module expanded by default

    const toggleModule = (moduleId: number) => {
        setExpandedModules(prev => {
            const next = new Set(prev);
            if (next.has(moduleId)) {
                next.delete(moduleId);
            } else {
                next.add(moduleId);
            }
            return next;
        });
    };

    const isModuleExpanded = (moduleId: number) => expandedModules.has(moduleId);

    useEffect(() => {
        fetchRoles();
    }, []);

    const fetchRoles = async () => {
        setLoading(true);
        try {
            const data = await RoleService.getAllRoles();
            setRoles(data || []);
        } catch (error) {
            console.error("Error fetching roles:", error);
            showMessage("Error", "Failed to load roles", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleRoleChange = async (role: Role) => {
        setSelectedRole(role);
        setLoading(true);
        try {
            const response = await RoleService.GetRolePermissionsAsync(role.roleId);

            let rawModules: any[] = [];
            if (Array.isArray(response)) {
                rawModules = response;
            } else if (response && Array.isArray(response.data)) {
                rawModules = response.data;
            } else if (response && Array.isArray(response.result)) {
                rawModules = response.result;
            } else {
                console.warn("Unexpected API response structure:", response);
            }

            // Map API response (view, create...) to internal state (canView, canCreate...)
            // Map API response to internal state using helper functions
            const mappedModules: Module[] = rawModules.map((mod: any) => ({
                moduleId: mod.moduleId,
                moduleName: mod.moduleName,
                displayName: mod.displayName,
                icon: mod.icon,
                displayOrder: mod.displayOrder,
                menus: (mod.menus || []).map(mapToMenu)
            }));

            setModules(mappedModules);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            showMessage("Error", "Failed to load permissions", "error");
            setModules([]);
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (
        label: string,
        message: string,
        severity: "success" | "info" | "warn" | "error"
    ) => {
        toast.current?.show({
            severity,
            summary: label,
            detail: message,
            life: 3000,
        });
    };

    const toggleMenuCollapse = (moduleId: number, menuId: number) => {
        const key = `${moduleId}-${menuId}`;
        setCollapsedMenus(prev => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    const isMenuCollapsed = (moduleId: number, menuId: number) => {
        return collapsedMenus.has(`${moduleId}-${menuId}`);
    };

    const isMenuFullyChecked = (moduleId: number, menuId: number) => {
        const parentMenu = modules.find(m => m.moduleId === moduleId)?.menus.find(m => m.menuId === menuId);
        if (!parentMenu || parentMenu.subMenu.length === 0) return false;

        return parentMenu.subMenu.every(hasAllPermissions);
    };

    const getMenuPermissionStats = (menu: Menu) => {
        const total = menu.subMenu.reduce((sum, sub) => sum + applicablePermissions(sub).length, 0);
        const selected = menu.subMenu.reduce((sum, sub) => sum + applicablePermissions(sub).filter(key => sub[key]).length, 0);
        return { selected, total, percentage: total > 0 ? Math.round(selected / total * 100) : 0 };
    };

    // Calculate overall permission stats
    const getOverallPermissionStats = () => {
        let totalSelected = 0;
        let totalPossible = 0;
        if (Array.isArray(modules)) {
            modules.forEach(mod => {
                mod.menus.forEach(menu => {
                    const stats = getMenuPermissionStats(menu);
                    totalSelected += stats.selected;
                    totalPossible += stats.total;
                });
            });
        }
        return { selected: totalSelected, total: totalPossible, percentage: totalPossible > 0 ? Math.round((totalSelected / totalPossible) * 100) : 0 };
    };

    const updateParent = (menu: Menu) => { menu.view = menu.subMenu.some(hasPermission); };
    const handlePermissionChange = (moduleIndex: number, menuIndex: number, subMenuIndex: number, field: PermissionKey, value: boolean) => {
        const updated: Module[] = modules.map(mod => ({ ...mod, menus: mod.menus.map(menu => ({ ...menu, subMenu: menu.subMenu.map(sub => ({ ...sub })) })) }));
        const menu = updated[moduleIndex].menus[menuIndex];
        setPermission(menu.subMenu[subMenuIndex], field, value);
        updateParent(menu);
        setModules(updated);
    };
    const handleToggleRow = (moduleIndex: number, menuIndex: number, subMenuIndex: number, checked: boolean) => {
        const updated: Module[] = modules.map(mod => ({ ...mod, menus: mod.menus.map(menu => ({ ...menu, subMenu: menu.subMenu.map(sub => ({ ...sub })) })) }));
        const menu = updated[moduleIndex].menus[menuIndex];
        setAllPermissions(menu.subMenu[subMenuIndex], checked);
        updateParent(menu);
        setModules(updated);
    };
    const handleToggleMenu = (moduleIndex: number, menuIndex: number, checked: boolean) => {
        const updated: Module[] = modules.map(mod => ({ ...mod, menus: mod.menus.map(menu => ({ ...menu, subMenu: menu.subMenu.map(sub => ({ ...sub })) })) }));
        const menu = updated[moduleIndex].menus[menuIndex];
        menu.subMenu.forEach(sub => setAllPermissions(sub, checked));
        updateParent(menu);
        setModules(updated);
    };
    const handleToggleColumn = (moduleIndex: number, menuIndex: number, field: PermissionKey, checked: boolean) => {
        const updated: Module[] = modules.map(mod => ({ ...mod, menus: mod.menus.map(menu => ({ ...menu, subMenu: menu.subMenu.map(sub => ({ ...sub })) })) }));
        const menu = updated[moduleIndex].menus[menuIndex];
        menu.subMenu.forEach(sub => setPermission(sub, field, checked));
        updateParent(menu);
        setModules(updated);
    };
    const areAllColumnChecked = (moduleIndex: number, menuIndex: number, field: PermissionKey) => {
        const rows = modules[moduleIndex].menus[menuIndex].subMenu.filter(sub => applicablePermissions(sub).includes(field));
        return rows.length > 0 && rows.every(sub => sub[field]);
    };

    const handleUndo = async () => {
        if (selectedRole) {
            await handleRoleChange(selectedRole);
            showMessage("Info", "Changes have been undone", "info");
        }
    };

    const onSave = async () => {
        if (!selectedRole) {
            showMessage("Warning", "Please select a role", "warn");
            return;
        }

        // Include disabled rows too, so an explicitly cleared action is persisted.
        const permissionsPayload: Array<{ RoleId: number; MenuId: number; CanView: boolean; CanCreate: boolean; CanEdit: boolean; CanDelete: boolean; CanExport: boolean; CanImport: boolean }> = [];
        const append = (menu: Menu | SubMenu) => {
            permissionsPayload.push({
                RoleId: selectedRole.roleId, MenuId: menu.menuId,
                CanView: menu.view, CanCreate: menu.create, CanEdit: menu.edit, CanDelete: menu.delete,
                CanExport: menu.supportsExport && menu.export,
                CanImport: menu.supportsImport && menu.import
            });
            menu.subMenu.forEach(append);
        };
        modules.forEach(mod => mod.menus.forEach(append));

        const requestBody = {
            RoleId: selectedRole.roleId,
            Permissions: permissionsPayload
        };

        try {
            setLoading(true);
            const result = await RoleService.UpdateRolePermissionsAsync(requestBody);
            if (!result?.isSuccess) throw new Error("Permissions were not saved.");
            MenuService.clearMenuCache();
            window.dispatchEvent(new Event("filePermissionsChanged"));
            showMessage("Success", "Permissions saved successfully", "success");
            // Refresh permissions to ensure UI is in sync with server
            if (selectedRole) {
                await handleRoleChange(selectedRole);
            }
        } catch (error: any) {
            console.error("Save failed", error);
            if (error.response && error.response.data) {
                console.error("Server Error Details:", error.response.data);
            }
            showMessage("Error", "Failed to save permissions", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="role-permissions-container flex flex-col min-h-screen font-sans bg-gray-50 pt-2 px-3 sm:px-4 md:px-6 pb-6">
            <Toast ref={toast} />
            <main className="flex-1 max-w-7xl mx-auto w-full animate-fade-in">

                {/* Modern Page Title */}
                <div className="card border-0 shadow-none bg-transparent">
                    {/* Page Title */}
                    <div className="feature-access-header">
                        <div className="feature-header-icon-wrapper">
                            <i className="pi pi-shield text-4xl"></i>
                        </div>
                        <div>
                            <h1 className="feature-header-title">
                                Role Permissions
                            </h1>
                            <p className="feature-header-subtitle">
                                Configure granulated access controls and secure workflow permissions for system roles.
                            </p>
                        </div>
                    </div>

                    {/* Header/Role Selection Filter Bar */}
                    <div className="feature-filter-bar">
                        <div className="filter-grid">
                            <div className="filter-field">
                                <label className="filter-label">
                                    <i className="pi pi-user"></i>
                                    ROLE
                                </label>
                                <Dropdown
                                    value={selectedRole}
                                    options={roles}
                                    onChange={(e) => handleRoleChange(e.value)}
                                    optionLabel="name"
                                    placeholder="Select Role"
                                    className="filter-dropdown w-full"
                                    panelClassName="role-permissions-dropdown-panel"
                                />
                            </div>

                            <div className="filter-actions">
                                {/* Undo Button (Outlined) */}
                                <Button
                                    label="Undo"
                                    icon="pi pi-undo"
                                    className="reset-btn"
                                    onClick={handleUndo}
                                    disabled={!selectedRole || modules.length === 0}
                                />
                                {/* Save Button (Green - like Filter) */}
                                <Button
                                    label="Save"
                                    icon="pi pi-check-circle"
                                    className="filter-btn"
                                    onClick={onSave}
                                    disabled={!selectedRole || modules.length === 0}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Loading State - AppLoading */}
                    {loading ? (
                        <AppLoading />
                    ) : !selectedRole ? (
                        <div className="empty-state-container">
                            <div className="empty-state-content">
                                <div className="empty-state-icon-wrapper">
                                    <i className="pi pi-shield text-4xl"></i>
                                </div>
                                <h3 className="empty-state-title">No Role Selected</h3>
                                <p className="empty-state-message">
                                    Select a role from the dropdown above to view and manage granular access permissions.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="animate-fade-in">
                            {/* Overall Permission Progress Summary */}
                            <div className="permission-summary-card">
                                <div className="summary-header">
                                    <div className="summary-icon">
                                        <i className="pi pi-shield "></i>
                                    </div>
                                    <div className="summary-info">
                                        <h3 className="summary-title">Permission Overview</h3>
                                        <p className="summary-subtitle">
                                            {getOverallPermissionStats().selected} of {getOverallPermissionStats().total} permissions enabled
                                        </p>
                                    </div>
                                    <div className="summary-percentage">
                                        <span className="percentage-value">{getOverallPermissionStats().percentage}%</span>
                                        <span className="percentage-label">Complete</span>
                                    </div>
                                </div>
                                <div className="summary-progress-bar">
                                    <div
                                        className="summary-progress-fill"
                                        style={{ width: `${getOverallPermissionStats().percentage}%` }}
                                    ></div>
                                </div>
                                <div className="summary-legend">
                                    <div className="legend-item">
                                        <i className="pi pi-eye legend-icon view"></i>
                                        <span>View</span>
                                    </div>
                                    <div className="legend-item">
                                        <i className="pi pi-plus-circle legend-icon create"></i>
                                        <span>Create</span>
                                    </div>
                                    <div className="legend-item">
                                        <i className="pi pi-file-edit legend-icon edit"></i>
                                        <span>Edit</span>
                                    </div>
                                    <div className="legend-item">
                                        <i className="pi pi-times-circle legend-icon delete"></i>
                                        <span>Delete</span>
                                    </div>
                                    <div className="flex align-items-center gap-2"><i className="pi pi-file-export" /><span>Export</span></div>
                                    <div className="flex align-items-center gap-2"><i className="pi pi-file-import" /><span>Import</span></div>
                                </div>
                            </div>

                            {/* Module Cards - Custom Expandable Sections */}
                            <div className="module-cards-container space-y-4">
                                {modules.map((module, modIndex) => {
                                    // Calculate module-level stats
                                    const moduleStats = module.menus.reduce((acc, menu) => {
                                        const stats = getMenuPermissionStats(menu);
                                        return { selected: acc.selected + stats.selected, total: acc.total + stats.total };
                                    }, { selected: 0, total: 0 });
                                    const modulePercentage = moduleStats.total > 0 ? Math.round((moduleStats.selected / moduleStats.total) * 100) : 0;
                                    const isOpen = isModuleExpanded(module.moduleId);

                                    return (
                                        <div key={module.moduleId} className="module-card">
                                            {/* Module Header */}
                                            <div
                                                className={`module-card-header ${isOpen ? 'expanded' : ''}`}
                                                onClick={() => toggleModule(module.moduleId)}
                                            >
                                                <div className="module-card-left">
                                                    <div className="module-card-icon">
                                                        <i className={`pi ${isOpen ? 'pi-folder-open' : 'pi-folder'}`}></i>
                                                    </div>
                                                    <div className="module-card-info">
                                                        <h3 className="module-card-title">{module.displayName}</h3>
                                                        <span className="module-card-subtitle">
                                                            {module.menus.length} menu{module.menus.length !== 1 ? 's' : ''} • {moduleStats.selected} permissions enabled
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="module-card-right">
                                                    {/* Progress Indicator */}
                                                    <div className="module-progress-ring">
                                                        <svg viewBox="0 0 36 36" className="circular-chart">
                                                            <path
                                                                className="circle-bg"
                                                                d="M18 2.0845
                                                                   a 15.9155 15.9155 0 0 1 0 31.831
                                                                   a 15.9155 15.9155 0 0 1 0 -31.831"
                                                            />
                                                            <path
                                                                className="circle-progress"
                                                                strokeDasharray={`${modulePercentage}, 100`}
                                                                d="M18 2.0845
                                                                   a 15.9155 15.9155 0 0 1 0 31.831
                                                                   a 15.9155 15.9155 0 0 1 0 -31.831"
                                                            />
                                                            <text x="18" y="20.35" className="percentage-text">{modulePercentage}%</text>
                                                        </svg>
                                                    </div>
                                                    <div className={`module-chevron ${isOpen ? 'open' : ''}`}>
                                                        <i className="pi pi-chevron-down"></i>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Module Content - Expandable */}
                                            <div className={`module-card-content ${isOpen ? 'expanded' : ''}`}>
                                                <div className="module-card-body">
                                                    {/* Sub-menus */}
                                                    {module.menus.map((menu, menuIndex) => {
                                                        const isCollapsed = isMenuCollapsed(module.moduleId, menu.menuId);

                                                        return (
                                                            <div key={menu.menuId} className="menu-card">
                                                                {/* Menu Header Row */}
                                                                <div
                                                                    className={`menu-card-header ${!isCollapsed ? 'expanded' : ''}`}
                                                                    onClick={() => toggleMenuCollapse(module.moduleId, menu.menuId)}
                                                                >
                                                                    <div className="menu-card-left">
                                                                        <div onClick={(e) => e.stopPropagation()}>
                                                                            <Checkbox
                                                                                icon="pi pi-check-circle"
                                                                                checked={isMenuFullyChecked(module.moduleId, menu.menuId)}
                                                                                onChange={(e) => handleToggleMenu(modIndex, menuIndex, e.checked || false)}
                                                                            />
                                                                        </div>
                                                                        <span className="menu-card-title">{menu.displayName}</span>
                                                                        <div className="menu-permission-badge">
                                                                            <span className="badge-count">{getMenuPermissionStats(menu).selected}</span>
                                                                            <span className="badge-separator">/</span>
                                                                            <span className="badge-total">{getMenuPermissionStats(menu).total}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="menu-card-right">
                                                                        <div className="menu-mini-progress">
                                                                            <div
                                                                                className="menu-mini-progress-fill"
                                                                                style={{ width: `${getMenuPermissionStats(menu).percentage}%` }}
                                                                            ></div>
                                                                        </div>
                                                                        <div className={`menu-chevron ${!isCollapsed ? 'open' : ''}`}>
                                                                            <i className="pi pi-chevron-down"></i>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Permissions Grid */}
                                                                <div className={`menu-card-content ${!isCollapsed ? 'expanded' : ''}`}>
                                                                    <div className="permissions-table">
                                                                        <table className="w-full">
                                                                            <thead>
                                                                                <tr className="permissions-header-row">
                                                                                    <th className="permission-th-name"> Name</th>
                                                                                    {[
                                                                                        { type: PermissionType.View, icon: 'pi-eye' },
                                                                                        { type: PermissionType.Create, icon: 'pi-plus-circle' },
                                                                                        { type: PermissionType.Edit, icon: 'pi-file-edit' },
                                                                                        { type: PermissionType.Delete, icon: 'pi-times-circle' },
                                                                                        { type: PermissionType.Export, icon: 'pi-file-export' },
                                                                                        { type: PermissionType.Import, icon: 'pi-file-import' }
                                                                                    ].map(perm => {
                                                                                        const permName = PermissionType[perm.type];
                                                                                        const fieldName = permName.toLowerCase() as PermissionKey;
                                                                                        return (
                                                                                            <th key={perm.type} className="permission-th">
                                                                                                <button
                                                                                                    type="button"
                                                                                                    className="permission-header-btn"
                                                                                                    onClick={() => handleToggleColumn(modIndex, menuIndex, fieldName, !areAllColumnChecked(modIndex, menuIndex, fieldName))}
                                                                                                    title={`Click to toggle all ${permName}`}
                                                                                                >
                                                                                                    <i className={`pi ${perm.icon}`}></i>
                                                                                                    <span>{permName}</span>
                                                                                                </button>
                                                                                            </th>
                                                                                        );
                                                                                    })}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {menu.subMenu.map((sub, subIndex) => {
                                                                                    const actions = applicablePermissions(sub);
                                                                                    const allSelected = hasAllPermissions(sub);
                                                                                    const someSelected = hasPermission(sub);
                                                                                    const permCount = actions.filter(key => sub[key]).length;
                                                                                    const totalPerms = actions.length;

                                                                                    return (
                                                                                        <tr key={sub.menuId} className={`permission-row ${allSelected ? 'full-access' : someSelected ? 'partial-access' : ''}`}>
                                                                                            <td className="permission-td-name">
                                                                                                <div className="submenu-row-content">
                                                                                                    <Checkbox
                                                                                                        icon="pi pi-check-circle"
                                                                                                        checked={allSelected}
                                                                                                        onChange={(e) => handleToggleRow(modIndex, menuIndex, subIndex, e.checked || false)}
                                                                                                    />
                                                                                                    <span className="submenu-name">{sub.displayName}</span>
                                                                                                    <div className={`row-status-indicator ${allSelected ? 'status-full' : someSelected ? 'status-partial' : 'status-none'}`}>
                                                                                                        {allSelected ? (
                                                                                                            <><i className="pi pi-check-circle"></i> Full</>
                                                                                                        ) : someSelected ? (
                                                                                                            <><i className="pi pi-minus-circle"></i> {permCount}/{totalPerms}</>
                                                                                                        ) : (
                                                                                                            <><i className="pi pi-circle"></i> None</>
                                                                                                        )}
                                                                                                    </div>
                                                                                                </div>
                                                                                            </td>
                                                                                            {([PermissionType.View, PermissionType.Create, PermissionType.Edit, PermissionType.Delete, PermissionType.Export, PermissionType.Import] as PermissionType[]).map((permTypeEnum, permIndex) => {
                                                                                                const permName = PermissionType[permTypeEnum].toLowerCase(); // 'view', 'create', ...
                                                                                                const permType = permName as PermissionKey;
                                                                                                const isChecked = !!sub[permType];
                                                                                                const permColors = permissionKeys;
                                                                                                const isDisabled = !actions.includes(permType);

                                                                                                if (isDisabled) {
                                                                                                    return (
                                                                                                        <td key={permType} className="permission-td">
                                                                                                            <div className={`permission-cell`}>
                                                                                                                <i
                                                                                                                    className="pi pi-times-circle"
                                                                                                                    style={{
                                                                                                                        fontSize: '22px',
                                                                                                                        color: '#ef4444',
                                                                                                                        opacity: 0.8
                                                                                                                    }}
                                                                                                                    title="Not Applicable"
                                                                                                                />
                                                                                                            </div>
                                                                                                        </td>
                                                                                                    );
                                                                                                }

                                                                                                return (
                                                                                                    <td key={permType} className="permission-td">
                                                                                                        <div className={`permission-cell ${permColors[permIndex]} ${isChecked ? 'active' : ''}`}>
                                                                                                            <Checkbox
                                                                                                                icon="pi pi-check-circle"
                                                                                                                aria-label={`${sub.displayName} ${permName}`}
                                                                                                                checked={isChecked}
                                                                                                                onChange={(e) => handlePermissionChange(modIndex, menuIndex, subIndex, permType, e.checked || false)}
                                                                                                            />
                                                                                                        </div>
                                                                                                    </td>
                                                                                                );
                                                                                            })}
                                                                                        </tr>
                                                                                    );
                                                                                })}
                                                                            </tbody>
                                                                        </table>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default FeatureAccessMain;
